import React from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Divider,
  Paper,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { useNavigate } from 'react-router-dom';
import './App.css';

// Data arrays
const plans = [
    {
        id: 'monthlystarter',
        name: 'Starter',
        price: '200',
        period: '/month',
        perks: [
          'Single Mailbox configuration only',
          'Monthly quota of 5,000 attachments',
          '$0.05 per additional attachment above quota',
          'Hosting, parsing & support',
        ],
      },
      {
        id: 'monthlypro',
        name: 'Pro',
        price: '600',
        period: '/month',
        perks: [
          'Monthly quota of 20,000 attachments included',
          '$0.04 per additional attachment above quota',
          'Document & image → PDF conversion',
          'Hosting, parsing & support',
        ],
      },
      {
        id: 'monthlyultimate',
        name: 'Ultimate',
        price: '1500',
        period: '/month',
        perks: [
          'Monthly quota of 100,000 attachments included',
          '$0.02 per additional attachment above quota',
          'Document & image → PDF conversion',
          'Hosting, parsing & support',
        ],
      },
      {
        id: 'monthlyenterprise',
        name: 'Enterprise',
        price: 'Custom',
        period: '/month',
        perks: [
          'Custom quota above 10 000 attachments included',
          'Custom price per additional attachment above quota',
          'Document & image → PDF conversion',
          'Hosting, parsing & support',
        ],
      },
  {
    id: 'setup',
    name: 'Initial Setup',
    price: '950',
    period: '(once-off)',
    perks: [
      'Mail & SharePoint folder configuration for one mailbox',
      '5 × Basic rules',
      '1 × Advanced rule',
    ],
  },
];

const addOns = [
  {
    name: 'Basic Rule Setup Support',
    price: '$50 each',
    perks: [
      'Filename/extension matching',
      'Simple metadata checks',
      '30 min functional testing',
    ],
  },
  {
    name: 'Advanced Rule Setup Support',
    price: '$350 each one off',
    perks: [
      'Download-link detection & fetching',
      'Conditional logic & retry handling',
      '1 h testing',
    ],
  },
  {
    name: 'OCR Invoice Exctraction',
    price: '$0.10 per page',
    perks: [
      'OCR & data extraction from invoices',

    ],
  },
];

export default function PricingInfo() {
  const navigate = useNavigate();

  return (
    <Paper elevation={3} sx={{ mt: 6, p: 4, bgcolor: 'background.paper' }}>
      <Container>
        <Box textAlign="center" mb={6}>
          <Typography variant="h3" gutterBottom>
            Pricing
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            Simple, transparent pricing built for any business size.
          </Typography>
        </Box>

        {/* Core Plans */}
        <Grid container spacing={4} justifyContent="center">
          {plans.map(plan => (
            <Grid item xs={12} md={4} key={plan.id}>
              {/* make the card full-height */}
              <Card
                className="card"
                elevation={3}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                }}
              >
                {/* let content grow */}
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    {plan.name}
                  </Typography>
                          <Box
                              mb={2}
                              display="flex"
                              flexDirection="column"
                              alignItems="flex-start"
                          >
                              <Typography variant="h2">
                                  ${plan.price}
                              </Typography>
                              <Typography variant="h6" color="textSecondary">
                                  {plan.period}
                              </Typography>
                          </Box>
                  <Divider className="rule" />
                  <List dense>
                    {plan.perks.map((p, i) => (
                      <ListItem key={i}>
                        <ListItemIcon>
                          <CheckIcon color="success" />
                        </ListItemIcon>
                        <ListItemText
                          className="list-item-text"
                          primary={p}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
                <Box p={2}>
                  <Button
                    className="button"
                    fullWidth
                    onClick={() => navigate('/signup')}
                  >
                    {plan.id === 'monthly' ? 'Subscribe' : 'Get Started'}
                  </Button>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Add-Ons */}
        <Box mt={10} textAlign="center">
          <Typography variant="h4" gutterBottom>
            Add-Ons
          </Typography>
          <Typography variant="subtitle2" color="textSecondary" mb={4}>
            Enhance your plan with these optional features
          </Typography>
          <Grid container spacing={4} justifyContent="center">
            {addOns.map((addOn, idx) => (
              <Grid item xs={12} md={4} key={idx}>
                <Card
                  className="card"
                  elevation={1}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      {addOn.name}
                    </Typography>
                    <Typography
                      variant="h5"
                      color="primary"
                      gutterBottom
                    >
                      {addOn.price}
                    </Typography>
                    <List dense>
                      {addOn.perks.map((p, i) => (
                        <ListItem key={i}>
                          <ListItemIcon>
                            <CheckIcon color="success" />
                          </ListItemIcon>
                          <ListItemText primary={p} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                  <Box p={2} textAlign="center">
                    <Button className="button" disabled>
                      Add
                    </Button>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Notes */}
        <Box mt={8} textAlign="center">
          <Typography variant="body2">
            Extra testing beyond included time: billed at $120/hr
          </Typography>
          <Typography variant="body2">
            Volume discount: 10% off when purchasing ≥ 5 additional rules
          </Typography>
        </Box>
      </Container>
      </Paper>
  );
}
